/*
  Scripts specific to Rek-AI implementation. 
  https://docs.rek.ai/
*/

interface RekAiResult {
  title: string;
  url: string;
  h1: string;
}

// Quicksearchs
var quickSearchFieldIds = [
  "page-search",
  "page-search-tablet",
  "page-search-sticky",
  "page-search-dw",
];

// Other
var searchPageSearchFieldIds = ["search-query", "publications-search-query"];

/*
  Gets the search page action url for the form
*/
export const getFormActionUrl = (id: string) => {
  var searchForm = document.getElementById(id)?.closest("form");
  return searchForm?.getAttribute("action");
};

function getPosition(string: string, subString: string, index: number) {
  return string.split(subString, index).join(subString).length;
}

const getFormSubTree = (id: string) => {
  const searchFormAction = document
    .getElementById(id)
    ?.closest("form")
    ?.getAttribute("action");
  if (searchFormAction && searchFormAction.split("/").length <= 3) return "/";

  if (!searchFormAction) return "/";

  return searchFormAction.substring(0, getPosition(searchFormAction, "/", 2));
};

interface RekAiPredictionItem {
  title: string;
  url: string;
}

interface RekAiPrediction {
  predictions: RekAiPredictionItem[];
}

function normalizeUrl(url: string) {
  try {
    const u = new URL(url, location.origin);
    // Normalize to origin + pathname (ignore query/hash; strip trailing slash)
    const pathname = u.pathname.replace(/\/+$/, "");
    return `${u.origin}${pathname}`;
  } catch {
    return url;
  }
}

function buildOnPageLinkSet() {
  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'));
  return new Set(anchors.map(a => normalizeUrl(a.href)));
}

function applyOnPageFilter(preds: RekAiPredictionItem[], params: { filter_exclude_onpagelinks?: boolean; filter_exclude_currentpage?: boolean; }) {
  if (!params.filter_exclude_onpagelinks) return preds;

  const onPage = buildOnPageLinkSet();
  const current = normalizeUrl(location.href);

  return preds.filter(p => {
    const n = normalizeUrl(p.url);
    if (params.filter_exclude_currentpage && n === current) return false;
    return !onPage.has(n);
  });
}
// Function for rendering predictions
function renderHTML(data: RekAiPrediction) {
  var rekaAiPredictionHolder = document.querySelector(".output");
  if (!rekaAiPredictionHolder) return;

  var noOfHitsToDisplay = parseInt(
    rekaAiPredictionHolder.getAttribute("data-nrofhits") || "5", 10
  );

  var noAddedLinks = 0;

  // Iterate recommendations
  for (var i = 0; i < data.predictions.length; i++) {
    if (noOfHitsToDisplay <= noAddedLinks) break;

    var rekAiPredictionListItem = document.createElement("li");

    var rekAIPredictionListItemLink = document.createElement("a");
    rekAIPredictionListItemLink.classList.add("rounded-link");
    rekAIPredictionListItemLink.setAttribute("href", data.predictions[i].url);
    rekAIPredictionListItemLink.innerText = data.predictions[i].title;

    var rekAiPredictionListItemIcon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    rekAiPredictionListItemIcon.classList.add("icon", "rounded-link__icon");
    rekAiPredictionListItemIcon.setAttribute("id", `rek-ai-icon--${i}`);

    rekAiPredictionListItemIcon.setAttribute("aria-hidden", "true");
    rekAiPredictionListItemIcon.setAttribute("width", "10");
    rekAiPredictionListItemIcon.setAttribute("height", "22");
    rekAiPredictionListItemIcon.setAttributeNS(null, "viewBox", "0 0 10 22");

    var rekAiPredictionListItemIconPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );

    rekAiPredictionListItemIconPath.setAttribute(
      "d",
      "M0 0h2l8 11-8 11H0l8-11z",
    );
    rekAiPredictionListItemIconPath.setAttribute("fill", "currentColor");
    rekAiPredictionListItemIconPath.setAttribute("fill-rule", "evenodd");

    rekAiPredictionListItemIcon.append(rekAiPredictionListItemIconPath);

    rekAIPredictionListItemLink.append(rekAiPredictionListItemIcon);
    rekAiPredictionListItem.append(rekAIPredictionListItemLink);

    rekaAiPredictionHolder.append(rekAiPredictionListItem);

    noAddedLinks++;
  }
}

/* 
  https://docs.rek.ai/advanced/allparameters/filter-parameters/filterexcludeonpagelinks
*/
const setupRekaiPrediction = () => {
  const holder = document.querySelector(".output");
  const excludedPats = holder?.getAttribute("data-excludetree") || "";
  const desired = parseInt(holder?.getAttribute("data-nrofhits") || "10", 10);

  const params: any = {
    addcontent: true,
    filter_exclude_onpagelinks: true,
    filter_exclude_currentpage: true,
    excludetree: excludedPats,
  };

  // Mirror Rek.ai over-fetch behavior to keep N after filtering
  let nr = desired*3; // compensate for on-page removal

  params.nrofhits = nr;

  window.__rekai?.predict({ params }, (data: RekAiPrediction) => {
    // Apply our own filter (since custom render bypasses Rek.ai’s internal filter)
    const filtered = applyOnPageFilter(data.predictions, params);

    // Render exactly `desired` items (if available)
    renderHTML({ ...data, predictions: filtered.slice(0, desired) });
  });
};

/*
  Setup for rek AI autocomplete
*/
export const setupRekAI = () => {
  // window.__rekai?.ready(() =>  {
  document.addEventListener("DOMContentLoaded", (e) => {
    var searchInputs = document.querySelectorAll('input[type="search"]');
    searchInputs.forEach((input) => {
      var id = input.getAttribute("id") as string;
      var rekAutocomplete = window
        .rekai_autocomplete(
          `#${id}`,
          {
            debug: false,
            params: {
              subtree: getFormSubTree(id),
              addcontent: true,
              openOnFocus: true,
            },
          },
          [
            {
              templates: {
                suggestion: function (suggestion: RekAiResult) {
                  return suggestion.h1;
                },
                empty: function () {
                  return '<div class="rekai-suggestion-empty">Din sökning gav inga träffar</div>';
                },
              },
            },
          ],
        )
        .on(
          "rekai_autocomplete:selected",
          function (event: Event, suggestion: RekAiResult, dataset: number) {
            if (quickSearchFieldIds.includes(id)) {
              var searchPageUrl = getFormActionUrl(id);
              window.location.href = `${searchPageUrl}?query=${encodeURIComponent(suggestion.title?.toLowerCase())}`;
            } else if (searchPageSearchFieldIds.includes(id)) {
              window.location.href = suggestion.url;
            }
          },
        );
    });

    setupRekaiPrediction();
  });
};

setupRekAI();
